import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Plus, Check } from "lucide-react";
import clsx from "clsx";

/**
 * Console-style project switcher. Lives in the TopBar next to the project name.
 * - Lists all of the user's projects with a check mark on the current one
 * - "+ NEW BLANK PROJECT" opens the creation dialog
 */
export default function ProjectSwitcher({ projects, currentProjectId, onSelect, onNewProject }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const filtered = projects.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const current = projects.find((p) => p.id === currentProjectId);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          "flex items-center gap-1.5 px-1.5 py-0.5 -mx-1.5 hover:bg-bg-elevated transition-colors",
          open && "bg-bg-elevated"
        )}
        data-testid="project-switcher-btn"
        title="Switch project"
      >
        <span className="font-medium text-sm text-ink truncate max-w-[160px] lg:max-w-[240px] xl:max-w-[280px]" data-testid="project-switcher-current">
          {current?.name || "—"}
        </span>
        <ChevronDown className={clsx("w-3 h-3 text-ink-mute transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div
          className="absolute left-0 mt-1 w-80 bg-bg-surface border border-line-strong shadow-[0_8px_32px_rgba(0,0,0,0.6)] z-50 animate-slide-up"
          data-testid="project-switcher-menu"
          role="menu"
        >
          <div className="px-3 py-2 border-b border-line text-2xs mono uppercase tracking-widest text-ink-mute flex items-center justify-between">
            <span>Projects · {projects.length}</span>
          </div>

          {projects.length > 6 && (
            <div className="p-2 border-b border-line-subtle">
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="w-full bg-bg-elevated text-xs p-1.5 outline-none border border-line focus:border-amber"
                data-testid="project-switcher-search"
              />
            </div>
          )}

          <div className="max-h-72 overflow-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-2xs mono uppercase tracking-widest text-ink-mute">— no matches —</div>
            )}
            {filtered.map((p) => {
              const isCurrent = p.id === currentProjectId;
              return (
                <button
                  key={p.id}
                  onClick={() => {
                    setOpen(false);
                    if (!isCurrent) onSelect(p.id);
                  }}
                  className={clsx(
                    "w-full flex items-center gap-2 px-3 py-2 text-left transition-colors border-b border-line-subtle last:border-b-0",
                    isCurrent ? "bg-bg-elevated" : "hover:bg-bg-elevated"
                  )}
                  data-testid={`project-switcher-item-${p.id}`}
                  role="menuitem"
                >
                  <Check className={clsx("w-3 h-3 shrink-0", isCurrent ? "text-amber" : "text-transparent")} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-ink truncate" title={p.name}>{p.name}</div>
                    <div className="mono text-2xs uppercase tracking-widest text-ink-mute mt-0.5">
                      {p.status || "planning"}{p.timezone ? ` · ${p.timezone}` : ""}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => {
              setOpen(false);
              onNewProject();
            }}
            className="w-full flex items-center gap-2 px-3 py-2 border-t border-line-strong bg-bg-elevated hover:bg-bg-highlight text-amber transition-colors"
            data-testid="project-switcher-new-btn"
            role="menuitem"
          >
            <Plus className="w-3 h-3" />
            <span className="mono text-2xs uppercase tracking-widest">New blank project</span>
          </button>
        </div>
      )}
    </div>
  );
}
